// Minimal API helper for Admin area
import { getApiBase, getBackendUrl } from '../utils/paths';

const API_BASE = getApiBase();

function withBase(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;

  let normalized = path;
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }

  if (normalized.startsWith('/uploads/') || normalized.startsWith('/backend/uploads/')) {
    return getBackendUrl(normalized);
  }
  if (normalized.startsWith('/vikash/uploads/')) return getBackendUrl(normalized.replace(/^\/vikash/, ''));
  if (normalized.startsWith('/vikash/backend/uploads/')) return getBackendUrl(normalized.replace(/^\/vikash/, ''));
  return normalized;
}

export { withBase };

async function parseApiResponse(res) {
  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  const text = await res.text();
  const isJson = contentType.includes('application/json');

  if (!res.ok) {
    if (isJson) {
      try {
        const data = JSON.parse(text);
        throw new Error(data.error || data.message || `Request failed with status ${res.status}`);
      } catch {
        throw new Error(`Request failed with status ${res.status}: ${text.trim().slice(0, 200)}`);
      }
    }
    throw new Error(`Request failed with status ${res.status}: ${text.trim().slice(0, 200)}`);
  }

  if (!isJson) {
    throw new Error(`Invalid server response; expected JSON but got ${contentType || 'plain text'}.`);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response from server: ${text.trim().slice(0, 200)}`);
  }
}

export async function adminLogin(email, password) {
  const res = await fetch(`${API_BASE}/auth/login.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Login failed");
  }
  return parseApiResponse(res);
}

function authHeaders() {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}`, "X-Auth-Token": token } : {};
}

export async function listCareers() {
  const res = await fetch(`${API_BASE}/careers/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load careers");
  return parseApiResponse(res);
}

export async function getCareer(id) {
  const res = await fetch(`${API_BASE}/careers/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch career");
  return parseApiResponse(res);
}

export async function createCareer(formData) {
  const res = await fetch(`${API_BASE}/careers/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to create career");
  }
  return parseApiResponse(res);
}

export async function deleteCareer(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/careers/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete career");
  return parseApiResponse(res);
}

export async function updateCareer(id, formData) {
  formData.append("_method", "PUT");
  formData.append("id", String(id));
  const res = await fetch(`${API_BASE}/careers/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to update career");
  return parseApiResponse(res);
}

export async function listInternships() {
  const res = await fetch(`${API_BASE}/internships/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load internships");
  return parseApiResponse(res);
}

export async function listInternshipApplicants() {
  const res = await fetch(`${API_BASE}/admin/internship-applications/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to load internship applicants");
  }
  return parseApiResponse(res);
}

export async function getInternship(id) {
  const res = await fetch(`${API_BASE}/internships/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch internship");
  return parseApiResponse(res);
}

export async function createInternship(formData) {
  const res = await fetch(`${API_BASE}/internships/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to create internship");
  }
  return parseApiResponse(res);
}

export async function deleteInternship(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/internships/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete internship");
  return parseApiResponse(res);
}

export async function updateInternship(id, formData) {
  formData.append("_method", "PUT");
  formData.append("id", String(id));
  const res = await fetch(`${API_BASE}/internships/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to update internship");
  return parseApiResponse(res);
}

export async function listBlogs() {
  const res = await fetch(`${API_BASE}/blogs/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load blogs");
  return parseApiResponse(res);
}

export async function getBlog(id) {
  const res = await fetch(`${API_BASE}/blogs/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch blog");
  return parseApiResponse(res);
}

export async function listApplicants() {
  const res = await fetch(`${API_BASE}/applicants/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load applicants");
  return parseApiResponse(res);
}

export async function listContactMessages() {
  const res = await fetch(`${API_BASE}/admin/contact-messages/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to load contact messages");
  }
  return parseApiResponse(res);
}

export async function updateContactMessage(id, status) {
  const form = new FormData();
  form.append("_method", "PUT");
  form.append("id", String(id));
  form.append("status", status);

  const res = await fetch(`${API_BASE}/admin/contact-messages/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to update contact message");
  }
  return parseApiResponse(res);
}

export async function deleteContactMessage(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));

  const res = await fetch(`${API_BASE}/admin/contact-messages/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to delete contact message");
  }
  return parseApiResponse(res);
}

export async function createBlog(formData) {
  const res = await fetch(`${API_BASE}/blogs/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to create blog");
  }
  return parseApiResponse(res);
}

export async function deleteBlog(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/blogs/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete blog");
  return parseApiResponse(res);
}

export async function updateBlog(id, formData) {
  formData.append("_method", "PUT");
  formData.append("id", String(id));
  const res = await fetch(`${API_BASE}/blogs/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to update blog");
  return parseApiResponse(res);
}

export async function listReports(type = '') {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const res = await fetch(`${API_BASE}/reports/index.php${query}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load reports");
  return parseApiResponse(res);
}

export async function getReport(id) {
  const res = await fetch(`${API_BASE}/reports/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch report");
  return parseApiResponse(res);
}

export async function createReport(formData) {
  const res = await fetch(`${API_BASE}/reports/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  return parseApiResponse(res);
}

export async function deleteReport(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/reports/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete report");
  return parseApiResponse(res);
}

export async function updateReport(id, formData) {
  formData.append("_method", "PUT");
  formData.append("id", String(id));
  const res = await fetch(`${API_BASE}/reports/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  return parseApiResponse(res);
}

export async function listEvents() {
  const res = await fetch(`${API_BASE}/events/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load events");
  return parseApiResponse(res);
}

export async function getEvent(id) {
  const res = await fetch(`${API_BASE}/events/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch event");
  return parseApiResponse(res);
}

export async function createEvent(formData) {
  const res = await fetch(`${API_BASE}/events/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to create event");
  }
  return parseApiResponse(res);
}

export async function deleteEvent(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/events/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete event");
  return parseApiResponse(res);
}

export async function updateEvent(id, formData) {
  formData.append("_method", "PUT");
  formData.append("id", String(id));
  const res = await fetch(`${API_BASE}/events/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to update event");
  return parseApiResponse(res);
}

export async function listGallery() {
  const res = await fetch(`${API_BASE}/gallery/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load gallery");
  return parseApiResponse(res);
}

export async function getGallery(id) {
  const res = await fetch(`${API_BASE}/gallery/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch gallery item");
  return parseApiResponse(res);
}

export async function createGallery(formData) {
  const res = await fetch(`${API_BASE}/gallery/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to create gallery item");
  }
  return parseApiResponse(res);
}

export async function deleteGallery(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/gallery/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete gallery item");
  return parseApiResponse(res);
}

export async function updateGallery(id, formData) {
  formData.append("_method", "PUT");
  formData.append("id", String(id));
  const res = await fetch(`${API_BASE}/gallery/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to update gallery item");
  return parseApiResponse(res);
}

// Donations API functions
export async function listDonations() {
  const res = await fetch(`${API_BASE}/donations/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load donations");
  return parseApiResponse(res);
}

export async function getDonation(id) {
  const res = await fetch(`${API_BASE}/donations/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch donation");
  return parseApiResponse(res);
}

export async function updateDonation(id, status, notes) {
  const form = new FormData();
  form.append("_method", "PUT");
  form.append("id", String(id));
  form.append("status", status);
  form.append("notes", notes);
  const res = await fetch(`${API_BASE}/donations/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to update donation");
  return parseApiResponse(res);
}

export async function deleteDonation(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/donations/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete donation");
  return parseApiResponse(res);
}

export async function listBankDetails() {
  const res = await fetch(`${API_BASE}/bank_details/index.php`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to load bank details");
  return parseApiResponse(res);
}

export async function getBankDetail(id) {
  const res = await fetch(`${API_BASE}/bank_details/index.php?id=${id}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error("Failed to fetch bank detail");
  return parseApiResponse(res);
}

export async function createBankDetail(formData) {
  const res = await fetch(`${API_BASE}/bank_details/index.php`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to create bank details");
  }
  return parseApiResponse(res);
}

export async function updateBankDetail(id, formData) {
  const res = await fetch(`${API_BASE}/bank_details/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: formData,
  });
  if (!res.ok) {
    const err = await parseApiResponse(res).catch(() => ({}));
    throw new Error(err.error || "Failed to update bank details");
  }
  return parseApiResponse(res);
}

export async function deleteBankDetail(id) {
  const form = new FormData();
  form.append("_method", "DELETE");
  form.append("id", String(id));
  const res = await fetch(`${API_BASE}/bank_details/index.php?id=${id}`, {
    method: "POST",
    headers: { ...authHeaders() },
    body: form,
  });
  if (!res.ok) throw new Error("Failed to delete bank detail");
  return parseApiResponse(res);
}

